import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Zap } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useUtilityTypes } from '@/hooks/use-utilities'
import {
  useBillingCycles,
  useGenerateInvoices,
  useGenerationRuns,
  useInvoices,
} from '@/hooks/use-billing'
import { useTabQueryParam } from '@/hooks/use-tab-query-param'
import { getApiErrorMessage } from '@/lib/api'
import { PlotReadingsChecklist, useReadingsProgress } from './plot-readings-checklist'
import { CycleInvoicesPanel } from './cycle-invoices-panel'
import {
  buildCycles,
  formatBillingPeriod,
  formatRunDate,
  RunStatusBadge,
} from './billing-cycle-shared'

type CycleTab = 'readings' | 'invoices'
const CYCLE_TABS: CycleTab[] = ['readings', 'invoices']

/** Breaks the generic "skipped" count into why — missing a reading, not billed for this plot's
 * classification, or already invoiced/no owner — so a partial run isn't reported as one vague
 * number that could be misread as "already invoiced" when it's really "still needs a reading". */
function describeSkipped(
  plotsSkipped: number,
  plotsPendingReading: number,
  plotsNotApplicable: number,
): string {
  const otherSkipped = plotsSkipped - plotsPendingReading - plotsNotApplicable
  const parts: string[] = []
  if (plotsPendingReading > 0) parts.push(`${plotsPendingReading} still need a reading`)
  if (plotsNotApplicable > 0) parts.push(`${plotsNotApplicable} not billed for this classification`)
  if (otherSkipped > 0) parts.push(`${otherSkipped} already invoiced`)
  return parts.join(', ')
}

export function CycleDetailPage({ basePath }: { basePath: string }) {
  const { utilityTypeId, billingPeriod } = useParams<{
    utilityTypeId: string
    billingPeriod: string
  }>()

  const { data: utilityData, isLoading: utilityLoading } = useUtilityTypes()
  const { data: runsData } = useGenerationRuns()
  const { data: cyclesData } = useBillingCycles()

  const utilityType = (utilityData?.data ?? []).find((t) => t.id === utilityTypeId)
  const cycle =
    utilityType && billingPeriod
      ? buildCycles(cyclesData?.data ?? [], runsData?.data ?? [], utilityType.id).find(
          (c) => c.period === billingPeriod,
        )
      : undefined

  const isMeterBased = utilityType?.billingMethod === 'meter_based'
  const progress = useReadingsProgress(utilityTypeId ?? '', billingPeriod ?? '')
  const generate = useGenerateInvoices(utilityTypeId ?? '', billingPeriod ?? '')
  const { data: invoicesData } = useInvoices({
    utilityTypeId,
    billingPeriod,
    excludeInvoiceType: 'credit_note',
    limit: 100,
  })
  const invoiceCount = invoicesData?.data.length ?? 0
  const [result, setResult] = useState<{
    invoicesCreated: number
    plotsSkipped: number
    plotsPendingReading: number
    plotsNotApplicable: number
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cycleTab, setCycleTab] = useTabQueryParam(CYCLE_TABS, 'readings')

  const backLink = (
    <Link
      to={basePath}
      className="inline-flex items-center gap-2 font-sans text-sm text-primary hover:text-green-lt"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Back to billing
    </Link>
  )

  if (utilityLoading) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-muted-foreground">Loading billing cycle…</p>
      </PageShell>
    )
  }

  if (!utilityType || !billingPeriod) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-muted-foreground">Billing cycle not found.</p>
        <div className="mt-4">{backLink}</div>
      </PageShell>
    )
  }

  const ready = isMeterBased ? progress.ready : true
  const readyMessage = !isMeterBased
    ? 'Flat-fee utility — no meter readings required. One invoice per occupied plot.'
    : progress.isLoading
      ? 'Checking reading progress…'
      : progress.total === 0
        ? 'No occupied plots found for this utility type.'
        : progress.billable === 0
          ? progress.entered === 0
            ? 'No plots have a reading yet — enter at least one to generate invoices.'
            : 'Nothing new to generate — every entered reading is already invoiced or needs correction.'
          : progress.complete
            ? 'All plot readings are entered — ready to generate.'
            : `${progress.billable} of ${progress.total} plot(s) ready to bill — the rest will be skipped this run.`

  async function handleGenerate() {
    setError(null)
    setResult(null)
    try {
      const { data } = await generate.mutateAsync({
        utilityTypeId: utilityType!.id,
        billingPeriod: billingPeriod!,
      })
      setResult({
        invoicesCreated: data.invoicesCreated,
        plotsSkipped: data.plotsSkipped,
        plotsPendingReading: data.plotsPendingReading,
        plotsNotApplicable: data.plotsNotApplicable,
      })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to generate invoices'))
    }
  }

  return (
    <PageShell>
      <PageHeader
        label="Finance"
        title={formatBillingPeriod(billingPeriod)}
        description={utilityType.name}
        action={backLink}
      />

      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <RunStatusBadge run={cycle?.latest ?? null} />
        </div>

        {cycle && cycle.history.length > 0 && (
          <div className="space-y-1.5 rounded border border-border-2 bg-cream-2 px-4 py-3">
            <p className="font-sans text-label uppercase tracking-[0.1em] text-muted-foreground">
              Previous attempts
            </p>
            {cycle.history.map((run) => (
              <div
                key={run.id}
                className="flex items-center justify-between gap-3 font-sans text-xs"
              >
                <span className="text-muted-foreground">
                  {formatRunDate(run.createdAt)} · {run.mode}
                </span>
                <div className="flex items-center gap-2">
                  {run.status === 'completed' && (
                    <span className="text-ink">{run.invoicesCreated} invoice(s)</span>
                  )}
                  <RunStatusBadge run={run} />
                </div>
              </div>
            ))}
            {cycle.history.some((run) => run.status === 'failed' && run.errorLog) && (
              <p className="font-sans text-xs text-destructive">
                {cycle.history.find((run) => run.status === 'failed')?.errorLog}
              </p>
            )}
            {cycle.latest?.status === 'completed' && (
              <p className="font-sans text-xs text-muted-foreground">
                {cycle.latest.plotsPendingReading > 0
                  ? `${cycle.latest.plotsPendingReading} plot(s) still need a reading — running again will pick them up once entered.`
                  : "Already generated — running again only creates invoices for plots that don't have one yet for this period."}
              </p>
            )}
          </div>
        )}

        {isMeterBased ? (
          <>
            <div className="flex gap-1 border-b border-border-2">
              <button
                onClick={() => setCycleTab('readings')}
                className={cn(
                  'px-4 py-2.5 font-sans text-sm font-medium transition-colors',
                  cycleTab === 'readings'
                    ? 'border-b-2 border-primary text-ink'
                    : 'text-muted-foreground hover:text-ink',
                )}
              >
                Meter readings{' '}
                <span className="text-muted-foreground">
                  {progress.entered}/{progress.total}
                </span>
              </button>
              <button
                onClick={() => setCycleTab('invoices')}
                className={cn(
                  'px-4 py-2.5 font-sans text-sm font-medium transition-colors',
                  cycleTab === 'invoices'
                    ? 'border-b-2 border-primary text-ink'
                    : 'text-muted-foreground hover:text-ink',
                )}
              >
                Invoices <span className="text-muted-foreground">{invoiceCount}</span>
              </button>
            </div>

            {cycleTab === 'readings' ? (
              <>
                <PlotReadingsChecklist utilityType={utilityType} billingPeriod={billingPeriod} />
                <div className="flex flex-col gap-3 rounded border border-border-2 bg-cream-2 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="font-sans text-sm text-muted-foreground">{readyMessage}</p>
                  <Button
                    disabled={!ready}
                    loading={generate.isPending}
                    onClick={() => void handleGenerate()}
                  >
                    <Zap size={16} className="mr-1.5" />
                    Generate Invoices
                  </Button>
                </div>
                {result && (
                  <p className="font-sans text-xs text-primary">
                    {result.invoicesCreated} invoice(s) generated
                    {result.plotsSkipped > 0 &&
                      `, ${describeSkipped(result.plotsSkipped, result.plotsPendingReading, result.plotsNotApplicable)}`}
                    .
                  </p>
                )}
                {error && (
                  <p className="font-sans text-xs text-destructive" role="alert">
                    {error}
                  </p>
                )}
              </>
            ) : (
              <CycleInvoicesPanel
                utilityTypeId={utilityTypeId ?? ''}
                billingPeriod={billingPeriod}
              />
            )}
          </>
        ) : (
          <>
            <div className="flex flex-col gap-3 rounded border border-border-2 bg-cream-2 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="font-sans text-sm text-muted-foreground">{readyMessage}</p>
              <Button
                disabled={!ready}
                loading={generate.isPending}
                onClick={() => void handleGenerate()}
              >
                <Zap size={16} className="mr-1.5" />
                Generate Invoices
              </Button>
            </div>
            {result && (
              <p className="font-sans text-xs text-primary">
                {result.invoicesCreated} invoice(s) generated
                {result.plotsSkipped > 0 &&
                  `, ${describeSkipped(result.plotsSkipped, result.plotsPendingReading, result.plotsNotApplicable)}`}
                .
              </p>
            )}
            {error && (
              <p className="font-sans text-xs text-destructive" role="alert">
                {error}
              </p>
            )}
            <p className="font-sans text-label uppercase tracking-[0.1em] text-muted-foreground">
              Invoices generated this period
            </p>
            <CycleInvoicesPanel utilityTypeId={utilityTypeId ?? ''} billingPeriod={billingPeriod} />
          </>
        )}
      </div>
    </PageShell>
  )
}
